const EventEmitter = require('events');
import { getCookieValue } from './lib/util';
import { translateNode, translateLink } from './lib/shared-util';
import { DEFAULTS } from './lib/constants.js';
import { appUI } from './lib/app-ui';
import { uuid } from '../../shared/lib/uuid';

// translate keys without adding extra params
function translateKeys(obj) {
  const map = {
    externalIdentifier: 'external_identifier',
    canonicalNodeUUID: 'canonical_node_uuid',
    parentId: 'parent_node_uuid',
  };
  const newObj = {};
  for (let key in obj) {
    if (map[key]) {
      newObj[ map[key] ] = obj[key];
    } else {
      newObj[key] = obj[key];
    }
  }
  return newObj;
}

class MapOperation extends EventEmitter {

  constructor({ sender, store, access }) {
    super();
    this.sender = sender;
    this.disconnected = false;
    this.store = store;
    this.access = access;
    this.log = [];
    this.pending = [];
    this.previousSequence = null;
    this.drainingPending = false;
    this.ur = new UndoRedo();
    this.messages = new Messages();

    this.isDisconnected = () => {
      return this.disconnected;
    };

    this.isLoggedOut = () => {
      return this.loggedOut;
    };

    this.setHistoryUI = hui => {
      this.hui = hui;
      this.sender.setHistoryUI(hui);
    };

    this.applyOperationsAfterSequence = async ({ sequence }) => {

      if (!window.user) {
        return;
      }
      if (typeof(sequence) !== 'number') {
        console.error(`invalid sequence: ${sequence}`);
        return;
      }
      const message = {
        type: 'operation',
        operation: 'getOperationsAfterSequence',
        opId: uuid(),
        sequence,
      };
      const response = await this.sender.send(message);
      if (response.status == 'failure') return;

      if (response && response.operations && response.operations.length) {
        for (let operation of response.operations) {
          const message = JSON.parse(operation.operation);
          message._applyingMissing = true;
          await this.operationHandler(message);
          this.previousSequence = operation.id;
        }
      }
    };

    this.operationHandler = async (message, deferUpdate) => {

      let store = this.store;

      // don't do any funny business if this is an operation coming from
      // playback mode, in which case historyUI is operating on a cloned store
      // and managing UI updates.
      if (this.hui && !message._fromPlayback) {

        // if we're in playback mode, and this operation isn't coming from
        // playback, it's a remote operation that we need to apply to the
        // canonical store.
        if (this.hui.inPlayback && this.hui.canonicalStore) store = this.hui.canonicalStore;

        // if we're inside of undo/redo, sender.send() will take care of our collection.
        if (!this.ur.inUndoOrRedo) {
          try {
            this.hui.operationCollector(JSON.parse(JSON.stringify(message)));
          } catch (e) {
            console.log(`couldn't collect ${message.operation}: ${e}`);
          }
        }
      }

      // don't transmit operations if we're in view-only mode
      if (!message._inBatchOperation) {
        if (!message._applyingMissing && !this.ur.inUndoOrRedo && !message._fromPlayback && !message.skipApplyOperationsAfterSequence) {
          if (this.previousSequence && typeof(message._previousSequence) !== 'undefined'
                && (this.previousSequence != message._previousSequence)) {
            console.log(`applying missing operations after ${this.previousSequence}`);
            await this.applyOperationsAfterSequence({ sequence: this.previousSequence });
            return;
          }
        }

        this.previousSequence = message._sequence;
      }
      switch (message.operation) {
        case 'setMapTitle': {
          store.setTitle(message.title);
          if (!deferUpdate) this.emit('operation-set-map-title', message);
          return message;
        }
        case 'addNode': {
          store.addNode(message.node);
          if (!deferUpdate) this.emit('operation-add-node', message);
          return message;
        }
        case 'deleteNodes': {

          const parentIds = [];
          const seenParentIds = {};
        // keep track of the parents of any deleted nodes so we can update them later
          message.nodeIds.forEach(id => {
            const node = store.getNode(id);
            if (node && node.parentId && !seenParentIds[node.parentId]) {
              parentIds.push(node.parentId);
              seenParentIds[node.parentId] = 1;
            }
          });
        // keep track of link nodes that were on the links being deleted
          const linkNodeIds = [];
          if (message.linkIds) {
            message.linkIds.forEach(id => {
              const link = store.getLink(id);
              if (link && link.linkNodeId) {
                linkNodeIds.push(link.linkNodeId);
              }
            });
          }

          message._localParentIds = parentIds;
          message._localLinkNodeIds = linkNodeIds;
          const result = store.deleteNodes({
            nodeIds: message.nodeIds,
            linkIds: message.linkIds,
          });
          if (result) {
            store.deleteFromAllPerspectives({
              nodeIds: result.deletedNodeIds,
              linkIds: result.deletedLinkIds
            });
            message.deletedNodeIds = result.deletedNodeIds;
            message.deletedLinkIds = result.deletedLinkIds;
            message.deletedLinkNodesMap = result.deletedLinkNodesMap;
            if (!deferUpdate) this.emit('operation-delete-nodes', message);
          } else {
            message.deletedNodeIds = [];
            message.deletedLinkIds = [];
            message.deletedLinkNodesMap = {};
          }
          return message;
        }
        case 'undeleteNodes': {
          const result = store.undeleteNodes({
            deletedNodeIds: message.deletedNodeIds,
            deletedLinkIds: message.deletedLinkIds,
            deletedLinkNodesMap: message.deletedLinkNodesMap,
          });
          if (result) {
            store.undeleteFromAllPerspectives({
              nodeIds: result.undeletedNodeIds,
              linkIds: result.undeletedLinkIds
            });
            message.undeletedNodeIds = result.undeletedNodeIds;
            message.undeletedLinkIds = result.undeletedLinkIds;
            if (!deferUpdate) this.emit('operation-undelete-nodes', message);
          } else {
            message.undeletedNodeIds = [];
            message.undeletedLinkIds = [];
          }
          return message;
        }
        case 'addLink': {
          store.addLink(message.link);
          if (!deferUpdate) this.emit('operation-add-link', message);
          return message;
        }
        case 'deleteLink': {
          store.deleteLink(message.linkId);
          store.undeleteFromAllPerspectives({
            linkIds: [message.linkId]
          });
          if (!deferUpdate) this.emit('operation-delete-link', message);
          return message;
        }
        case 'undeleteLink': {
          store.undeleteLink(message.linkId);
          if (!deferUpdate) this.emit('operation-undelete-link', message);
          return message;
        }
        case 'setLinkDirection': {
          const link = store.getLink(message.linkUUID);
          link.setDirection(message.direction);
          if (!deferUpdate) this.emit('operation-set-link-direction', message);
          return message;
        }
        case 'setLinkColor': {
          const link = store.getLink(message.linkUUID);
          link.setColor(message.color);
          if (!deferUpdate) this.emit('operation-set-link-color', message);
          return message;
        }
        case 'setLinkType': {
          const link = store.getLink(message.linkUUID);
          link.setLinkType(message.lineType);
          if (!deferUpdate) this.emit('operation-set-link-type', message);
          return message;
        }
        case 'setLinkWidth': {
          const link = store.getLink(message.linkUUID);
          link.setLinkWidth(message.lineWidth);
          if (!deferUpdate) this.emit('operation-set-link-width', message);
          return message;
        }
        case 'setLinkCurvature': {
          const link = store.getLink(message.linkUUID);
          link.setLinkCurvature(message.lineCurvature);
          if (!deferUpdate) this.emit('operation-set-link-curvature', message);
          return message;
        }
        case 'setLinkAngles': {
          const link = store.getLink(message.linkUUID);
          link.setAngles({
            angleStartToR: message.angleStartToR,
            angleEndToR: message.angleEndToR,
          });
          if (!deferUpdate) this.emit('operation-set-link-angles', message);
          return message;
        }
        case 'setLinkEndpoints': {
          const link = store.getLink(message.linkUUID);
          link.setEndpoints({
            fromNodeId: message.fromNodeId,
            toNodeId: message.toNodeId,
          });
          if (!deferUpdate) this.emit('operation-set-link-endpoints', message);
          return message;
        }
        case 'setLinkNodeId': {
          const link = store.getLink(message.link.id);
          link.setLinkNodeId(message.link.linkNodeId);
          link.placeLinkNode();
          if (!deferUpdate) this.emit('operation-set-link-node-id', message);
          return message;
        }
        case 'toggleLinkPreviewHidden': {
          if (!deferUpdate) this.emit('operation-toggle-link-preview-hidden', message);
          return message;
        }
        case 'toggleLinkPreviewCollapsed': {
          if (!deferUpdate) this.emit('operation-toggle-link-preview-collapsed', message);
          return message;
        }
        case 'setChildrenDetached': {
          store.getNode(message.nodeUUID).setChildrenDetached(message.detached);
          store.getNode(message.nodeUUID).setChildrenPosition({ x: message.x, y: message.y });
          if (!deferUpdate) this.emit('operation-set-children-detached', message);
          return message;
        }
        case 'setNodeBackgroundColor': {
          store.getNode(message.nodeUUID).setBackgroundColor(message.color);
          if (!deferUpdate) this.emit('operation-set-node-background-color', message);
          return message;
        }
        case 'setNodeDisplayStyle': {
          store.getNode(message.nodeUUID).setDisplayStyle(message.style);
          if (!deferUpdate) this.emit('operation-set-node-display-style', message);
          return message;
        }
        case 'setNodeLayoutName': {
          store.getNode(message.nodeUUID).setLayoutName(message.layoutName);
          if (!deferUpdate) this.emit('operation-set-layout-name', message);
          return message;
        }
        case 'setChildrenPosition': {
          store.getNode(message.nodeUUID).setChildrenPosition({ x: message.x, y: message.y, userWidth: message.userWidth, userHeight: message.userHeight });
          if (!deferUpdate) this.emit('operation-set-children-position', message);
          return message;
        }
        case 'editNode': {
          const nodeToEdit = store.getNode(message.nodeUUID);
          if (message.updates.canonical_node_uuid) {
            message.updates.canonicalNodeUUID = message.updates.canonical_node_uuid;
            delete message.updates.canonical_node_uuid;
          }
          if (nodeToEdit) {
            nodeToEdit.updateNode(message.updates);
          }
          if (!deferUpdate) this.emit('operation-edit-node', message);
          return message;
        }
        case 'addFileToNode': {
          store.getNode(message.nodeUUID)
          .updateNode({
            attachedFile: {
              file_url: message.fileUrl,
              name: message.name,
              file_type: message.fileType,
              uuid: message.fileUUID,
              preview_url: message.previewFileUrl,
              display_properties: message.displayProperties,
              file_attributes: message.fileAttributes,
            }
          });
          if (!deferUpdate) this.emit('operation-add-file-to-node', message);
          return message;
        }
        case 'rotateNodeFile': {
          store.getNode(message.nodeUUID)
          .updateNode({
            attachedFile: {
              file_url: message.fileUrl,
              uuid: message.fileUUID,
              name: message.name,
              file_type: message.fileType,
              preview_url: message.previewFileUrl,
              display_properties: message.displayProperties,
              file_attributes: message.fileAttributes,
            }
          });
          if (!deferUpdate) this.emit('operation-rotate-node-file', message);
          return message;
        }
        case 'updateNodeFile': {
          store.getNode(message.nodeUUID)
          .updateNode({
            attachedFile: Object.assign(
              {},
              store.getNode(message.nodeUUID).attachedFile,
              { display_properties: message.displayProperties }
            ),
          });
          if (!deferUpdate) this.emit('operation-update-node-file', message);
          return message;
        }
        case 'deleteNodeFile': {
          store.getNode(message.nodeUUID).updateNode({ attachedFile: null });
          if (!deferUpdate) this.emit('operation-delete-node-file', message);
          return message;
        }
        case 'undeleteNodeFile': {
          store.getNode(message.nodeUUID)
          .updateNode({
            attachedFile: {
              file_url: message.fileUrl,
              uuid: message.fileUUID,
              file_type: message.fileType,
              file_size: message.fileSize,
              name: message.name,
              preview_url: message.previewFileUrl,
              display_properties: message.displayProperties,
              file_attributes: message.fileAttributes,
            }
          });
          if (!deferUpdate) this.emit('operation-undelete-node-file', message);
          return message;
        }
        case 'updateNodeDimensions': {
          const node = store.getNode(message.nodeUUID);
          if (node) {
            node.setUserDimensions({ width: message.userWidth || null });
            node.setUserDimensions({ height: message.userHeight || null });
          }
          this.emit('operation-update-node-dimensions', message);
          return message;
        }
        case 'updateNodeJustification': {
          const node = store.getNode(message.nodeUUID);
          if (node) {
            node.setJustification(message.justification);
          }
          this.emit('operation-update-node-justification', message);
          return message;
        }
        case 'updateNodePositions': {
          for (const update of message.nodePositions) {
            const node = store.getNode(update.uuid);
            if (node) {
              node.updateNode(update);
            }
          }
          for (const update of message.linkAngles) {
            const link = store.getLink(update.uuid);
            if (link) {
              link.setAngles(update);
            }
          }
          if (!deferUpdate) this.emit('operation-update-node-positions', message);
          return message;
        }
        case 'createPresentation': {
          store.addPresentation({
            id: message.uuid,
            title: message.title,
            createdByUserId: message.createdByUserId,
            waypoints: message.waypoints,
          });
          if (!deferUpdate) this.emit('operation-create-presentation', message);
          return message;
        }
        case 'addPresentationWaypoint': {
          const presentation = store.getPresentation();
          if (presentation) {
            presentation.addWaypoint(message.waypoint);
          }
          if (!deferUpdate) this.emit('operation-add-presentation-waypoint', message);
          return message;
        }
        case 'addPresentationWaypoints': {
          const presentation = store.getPresentation();
          if (presentation) {
            for (const waypoint of message.waypoints) {
              presentation.addWaypoint(waypoint);
            }
          }
          if (!deferUpdate) this.emit('operation-add-presentation-waypoints', message);
          return message;
        }
        case 'removePresentationWaypoint': {
          const presentation = store.getPresentation();
          if (presentation) {
            presentation.removeWaypoint({ id: message.waypoint.uuid });
          }
          if (!deferUpdate) this.emit('operation-remove-presentation-waypoint', message);
          return message;
        }
        case 'removePresentationWaypoints': {
          const presentation = store.getPresentation();
          if (presentation) {
            for (const waypoint of message.waypoints) {
              presentation.removeWaypoint({ id: waypoint.uuid });
            }
          }
          if (!deferUpdate) this.emit('operation-remove-presentation-waypoints', message);
          return message;
        }
        case 'clearPresentationWaypoints': {
          const presentation = store.getPresentation();
          if (presentation) {
            presentation.clearWaypoints();
          }
          if (!deferUpdate) this.emit('operation-clear-presentation-waypoints', message);
          return message;
        }
        case 'updatePresentationWaypointOrderIndexes': {
          const presentation = store.getPresentation();
          if (presentation) {
            message.idIndexMappings.forEach(({ id, orderIndex }) => {
              const waypoint = presentation.getWaypoint({ id });
              if (waypoint) {
                waypoint.orderIndex = orderIndex;
              }
            });
          }
          if (!deferUpdate) this.emit('operation-update-presentation-order-indexes', message);
          return message;
        }
        case 'updatePresentationWaypointTitle': {
          const presentation = store.getPresentation();
          if (presentation) {
            const { waypointUUID: id, title } = message;
            const waypoint = presentation.getWaypoint({ id });
            if (waypoint) {
              waypoint.setTitle({ title });
            }
          }
          if (!deferUpdate) this.emit('operation-update-presentation-waypoint-title', message);
          return message;
        }
        case 'updatePresentationWaypointNodeIds': {
          const presentation = store.getPresentation();
          if (presentation) {
            const { waypointUUID: id, nodeIds} = message;
            const waypoint = presentation.getWaypoint({ id });
            if (waypoint) {
              waypoint.setNodeIds({ nodeIds });
            }
          }
          if (!deferUpdate) this.emit('operation-update-presentation-waypoint-node-ids', message);
          return message;
        }
        case 'setCollapsedState': {
          const node = store.getNode(message.nodeUUID);
          node.setIsCollapsed(message.isCollapsed);
          node.setDescendantsHidden(message.isCollapsed);
          if (!deferUpdate) this.emit('operation-set-collapsed-state', message);
          return message;
        }
        case 'editPerspectivesOrderCollection': {
          store.updateLegendPerspectives(message.sortedPs);
          if (!deferUpdate) this.emit('operation-edit-perspective-index', message);
          return message;
        }
        /* @@ operation-store-action @@ */
        case 'addRegion': {
          store.addRegion({
            id: message.regionUUID,
            title: message.title,
            color: message.color,
            top: message.top,
            bottom: message.bottom,
            left: message.left,
            right: message.right,
          });
          if (!deferUpdate) this.emit('operation-add-region', message);
          return message;
        }
        case 'undeleteRegion': {
          /* behaves the same as add region on the front end for now */
          store.addRegion({
            id: message.regionUUID,
            title: message.title,
            color: message.color,
            top: message.top,
            bottom: message.bottom,
            left: message.left,
            right: message.right,
          });
          if (!deferUpdate) this.emit('operation-add-region', message);
          return message;
        }
        case 'updateRegion': {
          const region = store.getRegion(message.regionUUID);
          if (region) {
            region.update(message.updates);
          }
          if (!deferUpdate) this.emit('operation-update-region', message);
          return message;
        }
        case 'deleteRegion': {
          const region = store.getRegion(message.regionUUID);
          store.deleteRegion({id: message.regionUUID});
          message._deletedRegion = region;
          if (!deferUpdate) this.emit('operation-delete-region', message);
          return message;
        }
        case 'addPerspectivePoint': {
          store.addPerspective({
            id: message.perspectiveUUID,
            label: message.label,
            color: message.color,
            members: message.members,
            orderIndex: message.orderIndex,
            pointNodeUUID: message.pointMapNodeUUID
          });
          if (!deferUpdate) this.emit('operation-add-perspective-point', message);
          return message;
        }
        case 'deletePerspectivePoint': {
          const perspective = store.getPerspective(message.perspectiveUUID);
          store.deletePerspective(message.perspectiveUUID);
          message._deletedPerspective = perspective;
          if (!deferUpdate) this.emit('operation-delete-perspective-point', message);
          return message;
        }
        case 'undeletePerspectivePoint': {
          store.undeletePerspective(message.perspectiveUUID);
          const perspective = store.getPerspective(message.perspectiveUUID);
          message._undeletedPerspective = perspective;
          if (!deferUpdate) this.emit('operation-undelete-perspective-point', message);
          return message;
        }
        case 'updatePerspectivePoint': {
          const perspective = store.getPerspective(message.perspectiveUUID);
          if (perspective) {
            perspective.update(message.updates);
          }

          if (!deferUpdate) this.emit('operation-update-perspective-point', message);
          return message;
        }
        case 'addToPerspectiveView': {
          const perspective = store.getPerspective(message.perspectiveUUID);

          if (perspective) {
            if (message.nodeIds && message.nodeIds.length) {
              for (const id of message.nodeIds) {
                perspective.addMemberNode( store.getNode(id) );

                if (message.overrides && message.overrides.nodes && message.overrides.nodes[id]) {
                  if (message.overrides.nodes[id].title) {
                    perspective.overrideNodeTitle(id, message.overrides.nodes[id].title);
                  }
                  if (message.overrides.nodes[id].summary) {
                    perspective.overrideNodeTitle(id, message.overrides.nodes[id].summary);
                  }
                }
              }
              if (message.linkIds && message.linkIds.length) {
                for (const id of message.linkIds) {
                  perspective.addMemberLink( store.getLink(id) );
                }
              }
            }
          }
          if (!deferUpdate) this.emit('operation-add-to-perspective-view', message);
          return message;
        }
        case 'deleteFromPerspectiveView': {
          const perspective = store.getPerspective(message.perspectiveUUID);
          if (perspective) {
            if (message.nodeIds && message.nodeIds.length) {
              for (const id of message.nodeIds) {
                perspective.removeMemberNode(id);
              }
            }
            if (message.linkIds && message.linkIds.length) {
              for (const id of message.linkIds) {
                perspective.removeMemberLink(id);
              }
            }
          }
          if (!deferUpdate) this.emit('operation-delete-from-perspective-view', message);
          return message;
        }
        case 'undeleteFromPerspectiveView': {
          const perspective = store.getPerspective(message.perspectiveUUID);
          if (message.nodeIds && message.nodeIds.length) {
            for (const id of message.nodeIds) {
              perspective.undeleteMemberNode( id );
            }
          }
          if (message.linkIds && message.linkIds.length) {
            for (const id of message.linkIds) {
              perspective.undeleteMemberLink( id );
            }
          }
          if (!deferUpdate) this.emit('operation-undelete-from-perspective-view', message);
          return message;
        }
        case 'updatePerspectiveNodeViews': {

          if (message.nodeViews) {
            for (const nodeView of message.nodeViews) {
              const p = store.getPerspective(nodeView.perspectiveUUID);
              if (!p) {
                continue;
              }
              if (nodeView.title !== undefined) {
                p.setAnnotationTitle(nodeView.mapNodeUUID, nodeView.title);
              }
              if (nodeView.displayProperties) {
                const {x, y} = nodeView.displayProperties.annotationPosition || {};
                p.setNodeAnnotationPosition({
                  nodeId: nodeView.mapNodeUUID,
                  x,
                  y,
                });
              }
            }
          }
          if (!deferUpdate) this.emit('operation-update-perspective-node-views', message);
          return message;
        }
        case 'addMapNodeAttribute': {
          const node = store.getNode(message.mapNodeUUID);
          if (node) {
            const { role, name, value, uuid } = message;
            node.addAttribute({ id: uuid, role, name, value });
          }
          if (!deferUpdate) this.emit('operation-add-map-node-attribute', message);
          return message;
        }
        case 'removeMapNodeAttribute': {
          const node = store.getNode(message.mapNodeUUID);
          if (node) {
            // removal via undo won't have an attribute id but will have name & value
            // remove via delete button will have an "id"
            const { uuid, role } = message;
            node.removeAttribute({ id: uuid, role });
          }
          if (!deferUpdate) this.emit('operation-remove-map-node-attribute', message);
          return message;
        }
        case 'editMapNodeAttribute': {
          const node = store.getNode(message.mapNodeUUID);
          if (node) {
            const { uuid, role, name, value } = message;
            node.editAttribute({ id: uuid, role, name, value });
          }
          if (!deferUpdate) this.emit('operation-edit-map-node-attribute', message);
          return message;
        }
        case 'addComment': {
          const { comment } = message;
          store.addComment(comment);
          if (!deferUpdate) this.emit('operation-add-comment', message);
          return message;
        }
        case 'editComment': {
          const { uuid } = message;
          const comment = store.getComment(uuid);
          if (!comment) return;
          comment.setText(message.commentText);
          if (!deferUpdate) this.emit('operation-edit-comment', message);
          return message;
        }
        case 'resolveComment': {
          const { uuid } = message;
          const comment = store.getComment(uuid);
          if (!comment) return;
          comment.resolve();
          if (!deferUpdate) this.emit('operation-resolve-comment', message);
          return message;
        }
        case 'unresolveComment': {
          const { uuid } = message;
          const comment = store.getComment(uuid);
          if (!comment) return;
          comment.unresolve();
          if (!deferUpdate) this.emit('operation-unresolve-comment', message);
          return message;
        }
        case 'deleteComment': {
          const { uuid } = message;
          const comment = store.getComment(uuid);
          if (!comment) return;
          comment.delete();
          if (!deferUpdate) this.emit('operation-delete-comment', message);
          return message;
        }
        case 'undeleteComment': {
          const { uuid } = message;
          const comment = store.getComment(uuid);
          if (!comment) return;
          comment.undelete();
          if (!deferUpdate) this.emit('operation-undelete-comment', message);
          return message;
        }
        case 'updateCommentDisplayProperties': {
          const { uuid, displayProperties } = message;
          const comment = store.getComment(uuid);
          if (!comment) return;
          let votes;
          if (comment.displayProperties.votes && displayProperties.votes) {
            votes = Object.assign(comment.displayProperties.votes, displayProperties.votes);
          }
          comment.displayProperties = Object.assign(comment.displayProperties,displayProperties);
          comment.displayProperties.votes = votes;
          // TODO: use some module to merge nested objects

          if (!deferUpdate) this.emit('operation-update-comment-display-properties', message);
          return message;
        }
        case 'addBatch': {
        // add multiple items in one go (i.e. nodes & links)
          const nodes = message.nodes;
          const links = message.links;
          if (nodes) {
            nodes.forEach(node => {
              store.addNode(node);
            });
          }

          if (links) {
            links.forEach(link => {
              store.addLink(link);
            });
          }

          if (!deferUpdate) this.emit('operation-add-batch', message);
          return message;
        }
        case 'updateMapDisplayProperties': {
          store.updateMapDisplayProperties(message.displayProperties);
          if (!deferUpdate) this.emit('operation-update-map-display-properties', message);
          return message;
        }
        case 'updateMapShareSettings': {
          store.updateAccess(message.access);
          if (message.access && message.access.permission !== 'view') {
            // reset viewOnlyTriggered flag
            store.unsetViewOnly();
          }
          if (!deferUpdate) this.emit('operation-update-map-share-settings', message);
          return message;
        }
        case 'updateUserShareSettings': {
          if (message.sharedWithUserId && message.sharedWithUserId === window.user_id) {
            store.updateAccess(message.access);
            if (message.access && message.access.permission !== 'view') {
              // reset viewOnlyTriggered flag
              store.unsetViewOnly();
            }
          }

          if (!deferUpdate) this.emit('operation-update-user-share-settings', message);
          return message;
        }
        case 'shareMapWithUser': {
          if (!deferUpdate) this.emit('operation-share-map-with-user', message);
          return message;
        }
        case 'batchOperation': {
          if (message.messages && message.messages.length) {
            for (const msg of message.messages) {
              msg._inBatchOperation = true;
              if (message.user) {
                msg.user = message.user;
              }
              if (message.userId) {
                msg.userId = message.userId;
              }
              if (message._fromPlayback) {
                msg._fromPlayback = message._fromPlayback;
              }
              await this.operationHandler(msg, deferUpdate);
            }
          }
          return message;
        }
        default:
          console.log(`unknown operation: ${message.operation}`);
      }
    };

    this.nodeCountHandler = message => {
      this.emit('node-count-change', message);
    };

    this.sender.setHandler('operation', this.operationHandler);

    this.sender.setHandler('node-count', this.nodeCountHandler);


    this.sender.setHandler('logout', mess => {
      this.loggedOut = true;
      this.sender.loggedOut = true;
      this.sender.stopPinging();
      this.emit('logged-out');
    });

    this.sender.setHandler('alert', options =>
      this.emit('alert', options)
    );

    window.setInterval(async () => {
      if (this.sender.isOpen()) {
        if (this.disconnected) {
          // If we're coming back from a disconnect, we may have pending
          // operations that we tried to send, but never received a response
          // for.
          await this.drainPending();
          this.disconnected = false;
          this.store.disconnected = false;
          this.emit('reconnected');
          return;
        }
      } else {
        if (!this.disconnected) {
          // go offline
          this.disconnected = true;
          this.store.disconnected = true;
          this.emit('disconnected');
          return;
        }
      }

      // Now feels like a good time to check to see if our previousSequence
      // (the last operation we applied) matches the server supplied
      // 'currentSequence' from a pong.
      if (this.previousSequence && this.sender.currentSequence
            && (this.previousSequence < this.sender.currentSequence)) {
        await this.applyOperationsAfterSequence({ sequence: this.previousSequence });
      } else if (this.previousSequence === null && this.sender.currentSequence) {
        await this.applyOperationsAfterSequence({ sequence: 0 });
      }

    }, 250);
  }

  setMapTitle({ title, prevTitle }, onFailure) {
    const message = {
      type: 'operation',
      operation: 'setMapTitle',
      opId: uuid(),
      title,
      prevTitle,
      userId: window.user_id,
      mapId: window.map_id,
    };
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  addNode({ node, onSuccess, onFailure }) {
    const message = this.messages.addNode({node});
    this.ur.trackUndoable(message);
    this._send({ message, onSuccess, onFailure });
  }

  deleteNodes({ nodeIds, linkIds, deletedNodeIds, deletedLinkIds, deletedLinkNodesMap }, onFailure) {
    const message = this.messages.deleteNodes({ nodeIds, linkIds, deletedNodeIds, deletedLinkIds, deletedLinkNodesMap });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  undeleteNodes({ nodeIds, linkIds, deletedNodeIds, deletedLinkIds, deletedLinkNodesMap, undeletedNodeIds, undeletedLinkIds }, onFailure) {
    const message = this.messages.undeleteNodes({ nodeIds, linkIds, deletedNodeIds, deletedLinkIds, deletedLinkNodesMap, undeletedNodeIds, undeletedLinkIds });
    // undeleteNodes is never a fresh action, so we don't have to
    // trackUndoable here.
    this._send({ message, onFailure });
  }

  undeleteLink({ linkId, deletedNodeIds, deletedLinkIds, undeletedNodeIds, undeletedLinkIds }, onFailure) {
    const message = this.messages.undeleteLink({ linkId, deletedNodeIds, deletedLinkIds, undeletedNodeIds, undeletedLinkIds });
    // undeleteLink is never a fresh action, so we don't have to
    // trackUndoable here.
    this._send({ message, onFailure });
  }

  addLink({ link }, onFailure) {
    const message = this.messages.addLink({ link });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  deleteLink({ linkId, deletedNodeIds, deletedLinkIds, onFailure }) {
    const message = this.messages.deleteLink({ linkId });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setLinkDirection({ linkUUID, direction, prevDirection, onFailure }) {
    const message = this.messages.setLinkDirection({ linkUUID, direction, prevDirection });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setLinkColor({ linkUUID, color, prevColor, onFailure}) {
    const message = this.messages.setLinkColor({ linkUUID, color, prevColor });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setLinkType({linkUUID, lineType, prevLineType, onFailure}) {
    const message = this.messages.setLinkType({linkUUID, lineType, prevLineType});
    this.ur.trackUndoable(message);
    this._send({message, onFailure});
  }

  setLinkWidth({linkUUID, lineWidth, prevLineWidth, onFailure}) {
    const message = this.messages.setLinkWidth({linkUUID, lineWidth, prevLineWidth});
    this.ur.trackUndoable(message);
    this._send({message, onFailure});
  }

  setLinkCurvature({ linkUUID, lineCurvature, prevLineCurvature, onFailure }) {
    const message = this.messages.setLinkCurvature({ linkUUID, lineCurvature, prevLineCurvature });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setLinkEndpoints({ linkUUID, fromNodeId, toNodeId, prevFromNodeId, prevToNodeId, onFailure }) {
    const message = this.messages.setLinkEndpoints({ linkUUID, fromNodeId, toNodeId, prevFromNodeId, prevToNodeId });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setLinkAngles({ linkUUID, angleStartToR, angleEndToR, prevAngleStartToR, prevAngleEndToR, onFailure }) {
    const message = this.messages.setLinkAngles({ linkUUID, angleStartToR, angleEndToR, prevAngleStartToR, prevAngleEndToR });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setLinkNodeId({ link, prevLink, onFailure }) {
    const message = this.messages.setLinkNodeId({ link, prevLink });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  toggleLinkPreviewHidden({ nodeId, isHidden, url, mapId }) {
    const message = this.messages.toggleLinkPreviewHidden({ nodeId, isHidden, url, mapId });
    this.ur.trackUndoable(message);
    this._send({ message });
  }

  toggleLinkPreviewCollapsed({ nodeId, isCollapsed, url, mapId }) {
    const message = this.messages.toggleLinkPreviewCollapsed({ nodeId, isCollapsed, url, mapId });
    this.ur.trackUndoable(message);
    this._send({ message });
  }

  editNode({ nodeUUID, updates, prevValues, onFailure, onSuccess }) {
    const message = this.messages.editNode({ nodeUUID, updates, prevValues });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addFileToNode({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name, prevFile, onSuccess, onFailure }) {
    const message = this.messages.addFileToNode({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name, prevFile });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  rotateNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl,prevFile, onSuccess, onFailure }) {
    const message = this.messages.rotateNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, prevFile });
    //we can't undo a rotate :/
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  updateNodeFile({ nodeUUID, fileUUID, displayProperties, prevDisplayProperties, fileAttributes, onSuccess, onFailure }) {
    const message = this.messages.updateNodeFile({ nodeUUID, fileUUID, displayProperties, prevDisplayProperties, fileAttributes });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  deleteNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name, onSuccess, onFailure }) {
    const message = this.messages.deleteNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  undeleteNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name, onSuccess, onFailure }) {
    const message = this.messages.undeleteNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, name, previewFileUrl });
    //this is never a fresh action, so we don't need to trackUndoable
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }


  setChildrenDetached({ nodeUUID, detached, x, y, onFailure }) {
    const message = this.messages.setChildrenDetached({ nodeUUID, detached, x, y });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setNodeLayoutName({ nodeUUID, layoutName, prevLayoutName, onFailure }) {
    const message = this.messages.setNodeLayoutName({ nodeUUID, layoutName, prevLayoutName });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setNodeBackgroundColor({ nodeUUID, color, prevColor, onFailure }) {
    const message = this.messages.setNodeBackgroundColor({ nodeUUID, color, prevColor });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setNodeDisplayStyle({ nodeUUID, style, prevStyle, onFailure }) {
    const message = this.messages.setNodeDisplayStyle({ nodeUUID, style, prevStyle });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setChildrenPosition({ nodeUUID, x, y, prevX, prevY, userWidth, userHeight, prevUserWidth, prevUserHeight, onFailure }) {
    const message = this.messages.setChildrenPosition({ nodeUUID, x, y, prevX, prevY, userWidth, userHeight, prevUserWidth, prevUserHeight });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  updateNodePositions({ prevNodePositions, prevLinkAngles, nodePositions, linkAngles, selectedNodes, onFailure }) {
    const message = this.messages.updateNodePositions({ prevNodePositions, prevLinkAngles, nodePositions, linkAngles, selectedNodes });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  updateNodeDimensions({ prevUserWidth, prevUserHeight, userWidth, userHeight, nodeUUID }) {
    const message = this.messages.updateNodeDimensions({ prevUserWidth, prevUserHeight, userWidth, userHeight, nodeUUID });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  updateNodeJustification({ prevJustification, justification, nodeUUID }) {
    const onFailure = e => console.log(e);
    const message = this.messages.updateNodeJustification({ prevJustification, justification, nodeUUID });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  createPresentation({ presentation, onFailure }) {
    const message = this.messages.createPresentation(presentation);
    this._send({ message, onFailure });
  }

  addPresentationWaypoint({ presentationUUID, waypoint, onFailure }) {
    const message = this.messages.addPresentationWaypoint({ presentationUUID, waypoint });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  addPresentationWaypoints({ presentationUUID, waypoints, onFailure }) {
    const message = this.messages.addPresentationWaypoints({ presentationUUID, waypoints });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  removePresentationWaypoint({ presentationUUID, waypoint, onFailure }) {
    const message = this.messages.removePresentationWaypoint({ presentationUUID, waypoint });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  removePresentationWaypoints({ presentationUUID, waypoints, onFailure }) {
    const message = this.messages.removePresentationWaypoints({ presentationUUID, waypoints });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  clearPresentationWaypoints({ presentation, prevWaypoints, onFailure }) {
    const message = this.messages.clearPresentationWaypoints({
      presentationUUID: presentation.id,
      prevWaypoints,
    });

    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  updatePresentationWaypointOrderIndexes({ presentationUUID, prevIdIndexMappings, idIndexMappings, onFailure }) {
    const message = this.messages.updatePresentationWaypointOrderIndexes({
      presentationUUID,
      idIndexMappings,
      prevIdIndexMappings,
    });

    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  updatePresentationWaypointTitle({ presentationUUID, waypointUUID, title, prevTitle, onFailure }) {
    const message = this.messages.updatePresentationWaypointTitle({
      presentationUUID,
      waypointUUID,
      title,
      prevTitle,
    });

    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  updatePresentationWaypointNodeIds({ presentationUUID, waypointUUID, nodeIds, prevNodeIds, onFailure }) {
    const message = this.messages.updatePresentationWaypointNodeIds({
      presentationUUID,
      waypointUUID,
      nodeIds,
      prevNodeIds,
    });

    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  setCollapsedState({ nodeUUID, isCollapsed, onFailure }) {
    const message = this.messages.setCollapsedState({ nodeUUID, isCollapsed });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }


  editPerspectivesOrderCollection({ sortedPs, prevPs, onFailure }) {
    const message = this.messages.editPerspectivesOrderCollection({ sortedPs, prevPs });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure });
  }

  /* @@ operation-method @@ */

  addRegion({ regionUUID, title, color, top, left, bottom, right, onSuccess, onFailure }) {
    const message = this.messages.addRegion({ regionUUID, title, color, top, left, bottom, right });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  updateRegion({ regionUUID, updates, prevValues, onSuccess, onFailure }) {
    const message = this.messages.updateRegion({ regionUUID, updates, prevValues });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  deleteRegion({ regionUUID, title, color, top, left, bottom, right, onSuccess, onFailure }) {
    const message = this.messages.deleteRegion({ regionUUID, title, color, top, left, bottom, right });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  undeleteRegion({ regionUUID, title, color, top, left, bottom, right, onSuccess, onFailure }) {
    const message = this.messages.undeleteRegion({ regionUUID, title, color, top, left, bottom, right });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  /* add a new perspective point */
  addPerspectivePoint({ perspectiveUUID, label, members, color, orderIndex, pointMapNodeUUID, onSuccess, onFailure }) {
    const message = this.messages.addPerspectivePoint({ perspectiveUUID, label, color, members, orderIndex, pointMapNodeUUID });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }
  /* remove a perspective point */
  deletePerspectivePoint({ perspectiveUUID, onSuccess, onFailure }) {
    const message = this.messages.deletePerspectivePoint({ perspectiveUUID });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }
  /* undo deletion of a perspective point */
  undeletePerspectivePoint({ perspectiveUUID, onSuccess, onFailure }) {
    const message = this.messages.undeletePerspectivePoint({ perspectiveUUID });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  /*
    update values of a given perspective point
    updates: {label, color, orderIndex, pointMapNodeUUID, isDeleted}
  */
  updatePerspectivePoint({ perspectiveUUID, updates, prevValues, onSuccess, onFailure }) {
    const message = this.messages.updatePerspectivePoint({ perspectiveUUID, updates, prevValues });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  /* add nodes and links to a given perspective */
  addToPerspectiveView({ perspectiveUUID, nodeIds, linkIds, overrides, onSuccess, onFailure }) {
    const message = this.messages.addToPerspectiveView({ perspectiveUUID, nodeIds, linkIds, overrides });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }
  /* remove nodes and links from a given perspective */
  deleteFromPerspectiveView({ perspectiveUUID, nodeIds, linkIds, onSuccess, onFailure }) {
    const message = this.messages.deleteFromPerspectiveView({ perspectiveUUID, nodeIds, linkIds });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }
  /* remove nodes and links from a given perspective */
  undeleteFromPerspectiveView({ perspectiveUUID, nodeIds, linkIds, onSuccess, onFailure }) {
    const message = this.messages.undeleteFromPerspectiveView({ perspectiveUUID, nodeIds, linkIds });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  updatePerspectiveNodeViews({ nodeViews, prevNodeViews, onSuccess, onFailure }) {
    const message = this.messages.updatePerspectiveNodeViews({ nodeViews, prevNodeViews });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addCardRole({ role, onSuccess, onFailure }) {
    const message = this.messages.addCardRole({ role });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addCardRoleAttribute({ role, name, displayName, description, peculiarities, onSuccess, onFailure }) {
    const message = this.messages.addCardRoleAttribute({ role, name, displayName, description, peculiarities });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  getCardRoles({ onSuccess, onFailure }) {
    const message = this.messages.getCardRoles();
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addMapNodeRole({ nodeId, role, onSuccess, onFailure }) {
    const message = this.messages.addMapNodeRole({ nodeId, role });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  removeMapNodeRole({ nodeId, role, onSuccess, onFailure }) {
    const message = this.messages.removeMapNodeRole({ nodeId, role });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  getMapNodeRoles({ nodeId, onSuccess, onFailure }) {
    const message = this.messages.getMapNodeRoles({ nodeId });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addMapNodeAttribute({ uuid, mapNodeUUID, role, name, value, onSuccess, onFailure }) {
    const message = this.messages.addMapNodeAttribute({ uuid, mapNodeUUID, role, name, value });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  editMapNodeAttribute({ uuid, mapNodeUUID, role, name, value, prevName, prevValue, onSuccess, onFailure }) {
    const message = this.messages.editMapNodeAttribute({ uuid, mapNodeUUID, role, name, value, prevName, prevValue });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  removeMapNodeAttribute({ uuid, mapNodeUUID, name, value, role, onSuccess, onFailure }) {
    /* note: we include name, value, and role here to handle the undo action of this on the collaborator's side */
    const message = this.messages.removeMapNodeAttribute({ uuid, mapNodeUUID, name, value, role });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  getMapNodeAttributes({ nodeId, onSuccess, onFailure }) {
    const message = this.messages.getMapNodeAttributes({ nodeId });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addComment({ uuid, comment, onSuccess, onFailure }) {
    const message = this.messages.addComment({ uuid, comment });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  editComment({ uuid, commentText, prevCommentText, onSuccess, onFailure }) {
    const message = this.messages.editComment({ uuid, commentText, prevCommentText });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  deleteComment({ uuid, onSuccess, onFailure }) {
    const message = this.messages.deleteComment({ uuid });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  undeleteComment({ uuid, onSuccess, onFailure }) {
    const message = this.messages.undeleteComment({ uuid });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  resolveComment({ uuid, onSuccess, onFailure }) {
    const message = this.messages.resolveComment({ uuid });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  unresolveComment({ uuid, onSuccess, onFailure }) {
    const message = this.messages.unresolveComment({ uuid });
    // this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  updateCommentDisplayProperties({ uuid, displayProperties, prevDisplayProperties, onSuccess, onFailure }) {
    const message = this.messages.updateCommentDisplayProperties({ uuid, displayProperties, prevDisplayProperties });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  addBatch({ nodes, links, onFailure, onSuccess }) {
    const message = this.messages.addBatch({ nodes, links });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  updateMapDisplayProperties({displayProperties, prevDisplayProperties, onFailure, onSuccess}) {
    const message = this.messages.updateMapDisplayProperties({ displayProperties, prevDisplayProperties });
    this.ur.trackUndoable(message);
    this._send({ message, onFailure, onSuccess });
  }

  loadOperations({ onFailure, onSuccess }) {
    const message = this.messages.loadOperations();
    this._send({ message, onFailure, onSuccess });
  }

  /*
   * apply multiple operations together in one batch
   * - generate messages for each individual operation
   * - pass in a list of messages
   * - fire off the batchOperation
   */
  batchOperation({messages, onFailure, onSuccess, undoable=true}) {
    if (!messages || !messages.length) {
      return;
    }
    const message = {
      type: 'operation',
      operation: 'batchOperation',
      opId: uuid(),
      messages
    };
    if (undoable) {
      this.ur.trackUndoable(message);
    }
    this._send({ message, onFailure, onSuccess });
  }

  updateMapShareSettings({ field, value, onFailure }) {
    const message = {
      type: 'operation',
      operation: 'updateMapShareSettings',
      opId: uuid(),
      field,
      value,
    };

    this._send({ message, onFailure });
  }

  shareMapWithUser({ email, permission, onSuccess, onFailure }) {
    const message = this.messages.shareMapWithUser({ email, permission });
    this._send({ message, onFailure, onSuccess });
  }

  updateUserShareSettings({ sharedWithUserId, inviteId, permission, onSuccess, onFailure}) {
    const message = this.messages.updateUserShareSettings({ sharedWithUserId, inviteId, permission });
    this._send({ message, onFailure, onSuccess });
  }

  beginPending(message) {
    if (!this.drainingPending) {
      this.pending.push({ op: message, sent: Date.now() });
    }
  }

  finishPending(message) {
    if (!this.drainingPending) {
      const idx = this.pending.findIndex(p => p.op.opId == message.opId);
      const sent = this.pending.splice(idx, 1)[0].sent;
      // console.log(`opId ${message.opId} took ${Date.now() - sent} ms`);
    }
  }

  // We need to make sure one pending operation is completed before the next is
  // started, hence the async.  This seems okay here, as we're coming back from
  // a disconnected state, and won't say we're reconnected until all pending
  // operations have been drained.
  async drainPending() {
    this.drainingPending = true;
    // Catch up, first.
    await this.applyOperationsAfterSequence({ sequence: this.previousSequence });
    console.log('draining pending operations');
    while (this.pending.length) {
      const item = this.pending.shift();
      console.log('will drain item: ', item);
      const res = await this.sender.send(item.op);
      // TODO: handle errors
      console.log('drain status: ', res.status);
    }
    this.drainingPending = false;
  }

  _send({ message, onFailure, onSuccess }) {
    if (!message.type) {
      message.type = 'operation';
    }
    if (!message.opId ) {
      message.opId = uuid();
    }
    canvas.setSaving();
    this.beginPending(message);
    const prom = this.sender.send(message);
    if (!prom) return;
    prom.then(res => {
      this.finishPending(message);
      if (res.status === 'success') {
        this.previousSequence = res._sequence;

        if (Object.keys(this.sender.pendingOperations).length == 0) {
          canvas.setSaved();
        }
        if (typeof(onSuccess) === 'function') {
          onSuccess(res);
        }
      } else if (res.status === 'failure') {
        canvas.setSaveFailed();
        if (typeof(onFailure) === 'function') {
          onFailure(res.error);
        }
      }
    });
  }

  _filterNode(node) {
    // remove the _store property
    const { _store, ...newNode } = node;
    return newNode;
  };

  _filterLink(link) {
      // remove the _store property
    const { _store, ...newLink } = link;
    return newLink;
  };



  async undo() {
    let message = this.ur.undoStack.pop();
    if (!message) {
      return;
    }
    // disallow trackUndoable from manipulating the UndoRedo stacks while in an
    // undo.
    this.ur.inUndoOrRedo = true;

    // call the operationHandler to update the store and possibly
    // augement the message.
    message = await this.operationHandler(message);

    // invert our operation, make a new opId, and push it on
    // the redoStack
    const inverse = this.ur.invert(message);
    if (inverse) {
      inverse.opId = uuid();
      this.ur.redoStack.push(inverse);
      canvas.undoRedoTag.update();
      canvas.appFooterTag.update();
    } else {
      console.log(`there is no inverse for operation ${message.operation}`);
    }

    // do the database operation broadcast thing
    const opFunc = this[message.operation].bind(this);
    opFunc(message, e => { console.log(e); });

    this.ur.inUndoOrRedo = false;
  }

  async redo() {
    let message = this.ur.redoStack.pop();
    if (!message) {
      return;
    }
    this.ur.inUndoOrRedo = true;

    message = await this.operationHandler(message);

    const inverse = this.ur.invert(message);
    inverse.opId = uuid();
    this.ur.undoStack.push(inverse);
    canvas.undoRedoTag.update();
    canvas.appFooterTag.update();

    const opFunc = this[message.operation].bind(this);
    opFunc(message, e => { console.log(e); });

    this.ur.inUndoOrRedo = false;
  }

}


class Messages {
  constructor () { }

  addNode({ node }) {
    const newNode = translateNode(node);
    return {
      operation: 'addNode',
      node: newNode,
    };
  }

  deleteNodes({ nodeIds, linkIds, deletedNodeIds, deletedLinkIds, deletedLinkNodesMap }) {
    return {
      operation: 'deleteNodes',
      nodeIds,
      linkIds,
      deletedNodeIds,
      deletedLinkIds,
      deletedLinkNodesMap
    };
  }

  undeleteNodes({ nodeIds, linkIds, deletedNodeIds, deletedLinkIds, deletedLinkNodesMap, undeletedNodeIds, undeletedLinkIds }) {
    return {
      operation: 'undeleteNodes',
      nodeIds,
      linkIds,
      deletedNodeIds,
      deletedLinkIds,
      undeletedNodeIds,
      undeletedLinkIds,
      deletedLinkNodesMap,
    };
  }

  undeleteLink({ linkId, deletedNodeIds, deletedLinkIds, undeletedNodeIds, undeletedLinkIds }) {
    return {
      operation: 'undeleteLink',
      linkId,
    };
  }

  addLink({ link }) {
    const newLink = translateLink(link);
    return {
      operation: 'addLink',
      link: newLink,
    };
  }

  deleteLink({ linkId }) {
    return {
      operation: 'deleteLink',
      linkId,
    };
  }

  setLinkDirection({ linkUUID, direction, prevDirection }) {
    return {
      operation: 'setLinkDirection',
      linkUUID,
      direction,
      prevDirection,
    };
  }

  setLinkColor({ linkUUID, color, prevColor }) {
    return {
      operation: 'setLinkColor',
      linkUUID,
      color,
      prevColor,
    };
  }

  setLinkType({linkUUID, lineType, prevLineType}) {
    return {
      operation: 'setLinkType',
      linkUUID,
      lineType,
      prevLineType,
    };
  }

  setLinkWidth({linkUUID, lineWidth, prevLineWidth}) {
    return {
      operation: 'setLinkWidth',
      linkUUID,
      lineWidth,
      prevLineWidth,
    };
  }

  setLinkCurvature({ linkUUID, lineCurvature, prevLineCurvature }) {
    return {
      operation: 'setLinkCurvature',
      linkUUID,
      lineCurvature,
      prevLineCurvature,
    };
  }

  setLinkEndpoints({ linkUUID, fromNodeId, toNodeId, prevFromNodeId, prevToNodeId }) {
    return {
      operation: 'setLinkEndpoints',
      linkUUID,
      fromNodeId,
      toNodeId,
      prevFromNodeId,
      prevToNodeId,
    };
  }
  setLinkAngles({ linkUUID, angleStartToR, angleEndToR, prevAngleStartToR, prevAngleEndToR }) {
    return {
      operation: 'setLinkAngles',
      linkUUID,
      angleStartToR,
      angleEndToR,
      prevAngleStartToR,
      prevAngleEndToR,
    };
  }

  editNode({ nodeUUID, updates, prevValues }) {
    return {
      operation: 'editNode',
      updates: translateKeys(updates),
      prevValues,
      nodeUUID,
    };
  }

  rotateNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, prevFile }) {
    return {
      operation: 'rotateNodeFile',
      nodeUUID,
      fileUUID,
      fileUrl,
      fileType,
      fileSize,
      fileAttributes,
      displayProperties,
      previewFileUrl,
      prevFile,
    };
  }


  addFileToNode({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name, prevFile }) {
    return {
      operation: 'addFileToNode',
      nodeUUID,
      fileUUID,
      fileUrl,
      fileType,
      fileSize,
      fileAttributes,
      displayProperties,
      previewFileUrl,
      name,
      prevFile,
    };
  }

  updateNodeFile({ nodeUUID, fileUUID, displayProperties, prevDisplayProperties, fileAttributes }) {
    return {
      operation: 'updateNodeFile',
      nodeUUID,
      fileUUID,
      displayProperties,
      prevDisplayProperties,
      fileAttributes
    };
  }

  deleteNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name }) {
    return {
      operation: 'deleteNodeFile',
      nodeUUID,
      fileUUID,
      fileUrl,
      fileType,
      fileSize,
      fileAttributes,
      displayProperties,
      previewFileUrl,
      name,
    };
  }

  undeleteNodeFile({ nodeUUID, fileUUID, fileUrl, fileType, fileSize, fileAttributes, displayProperties, previewFileUrl, name }) {
    return {
      operation: 'undeleteNodeFile',
      nodeUUID,
      fileUUID,
      fileUrl,
      fileType,
      name,
      fileSize,
      fileAttributes,
      displayProperties,
      previewFileUrl,
    };
  }

  setLinkNodeId({ link, prevLink }) {
    return {
      operation: 'setLinkNodeId',
      link: translateLink(link),
      prevLink: translateLink(prevLink),
    };
  }

  toggleLinkPreviewHidden({ nodeId, isHidden, url, mapId }) {
    return {
      operation: 'toggleLinkPreviewHidden',
      nodeId,
      mapId,
      isHidden,
      url,
    };
  }

  toggleLinkPreviewCollapsed({ nodeId, isCollapsed, url, mapId }) {
    return {
      operation: 'toggleLinkPreviewCollapsed',
      nodeId,
      mapId,
      isCollapsed,
      url,
    };
  }

  setChildrenDetached({ nodeUUID, detached, x, y }) {
    return {
      operation: 'setChildrenDetached',
      detached,
      x,
      y,
      nodeUUID,
    };
  }

  setNodeBackgroundColor({ nodeUUID, color, prevColor }) {
    return {
      operation: 'setNodeBackgroundColor',
      color,
      prevColor,
      nodeUUID,
    };
  }

  setNodeDisplayStyle({ nodeUUID, style, prevStyle }) {
    return {
      operation: 'setNodeDisplayStyle',
      style,
      prevStyle,
      nodeUUID,
    };
  }

  setNodeLayoutName({ nodeUUID, layoutName, prevLayoutName }) {
    return {
      operation: 'setNodeLayoutName',
      layoutName,
      prevLayoutName,
      nodeUUID,
    };
  }

  setChildrenPosition({ nodeUUID, x, y, prevX, prevY, userWidth, userHeight, prevUserWidth, prevUserHeight }) {
    return {
      operation: 'setChildrenPosition',
      x,
      y,
      prevX,
      prevY,
      userWidth,
      userHeight,
      prevUserWidth,
      prevUserHeight,
      nodeUUID,
    };
  }

  updateNodePositions({ prevNodePositions, prevLinkAngles, nodePositions, linkAngles, selectedNodes }) {
    return {
      operation: 'updateNodePositions',
      prevNodePositions,
      prevLinkAngles,
      nodePositions,
      selectedNodes,
      linkAngles,
    };
  }

  updateNodeDimensions({ prevUserWidth, prevUserHeight, userWidth, userHeight, nodeUUID }) {
    return {
      operation: 'updateNodeDimensions',
      prevUserWidth,
      prevUserHeight,
      userWidth,
      userHeight,
      nodeUUID,
    };
  }

  updateNodeJustification({ justification, prevJustification, nodeUUID }) {
    return {
      operation: 'updateNodeJustification',
      prevJustification,
      justification,
      nodeUUID,
    };
  }

  createPresentation({ id, title, waypoints, createdByUserId }) {
    return {
      operation: 'createPresentation',
      uuid: id,
      title,
      waypoints: waypoints.map(w => { const { _store, ...clean } = w; clean.uuid = clean.id; return clean; }),
      createdByUserId,
      mapId: window.map_id,
    };
  }

  addPresentationWaypoint({ presentationUUID, waypoint }) {
    waypoint.uuid = waypoint.id;
    const { _store, ...clean } = waypoint;
    return {
      operation: 'addPresentationWaypoint',
      presentationUUID,
      waypoint: clean,
      mapId: window.map_id,
    };
  }

  addPresentationWaypoints({ presentationUUID, waypoints }) {
    return {
      operation: 'addPresentationWaypoints',
      presentationUUID,
      waypoints: waypoints.map(w => { const { _store, ...clean } = w; clean.uuid = clean.id; return clean; }),
      mapId: window.map_id,
    };
  }

  removePresentationWaypoint({ presentationUUID, waypoint }) {
    waypoint.uuid = waypoint.id;
    const { _store, ...clean } = waypoint;
    return {
      operation: 'removePresentationWaypoint',
      presentationUUID,
      waypoint: clean,
      mapId: window.map_id,
    };
  }

  removePresentationWaypoints({ presentationUUID, waypoints }) {
    return {
      operation: 'removePresentationWaypoints',
      presentationUUID,
      waypoints: waypoints.map(w => { const { _store, ...clean } = w; clean.uuid = clean.id; return clean; }),
      mapId: window.map_id,
    };
  }

  clearPresentationWaypoints({ presentationUUID, prevWaypoints }) {
    return {
      operation: 'clearPresentationWaypoints',
      presentationUUID,
      prevWaypoints: prevWaypoints.map(pw => { const { _store, ...clean } = pw; clean.uuid = clean.id; return clean; }),
      mapId: window.map_id,
    };
  }

  updatePresentationWaypointOrderIndexes({ presentationUUID, idIndexMappings, prevIdIndexMappings }) {
    return {
      operation: 'updatePresentationWaypointOrderIndexes',
      presentationUUID,
      idIndexMappings,
      prevIdIndexMappings,
      mapId: window.map_id,
    };
  }

  updatePresentationWaypointTitle({ presentationUUID, waypointUUID, title, prevTitle }) {
    return {
      operation: 'updatePresentationWaypointTitle',
      presentationUUID,
      waypointUUID,
      title,
      prevTitle,
      mapId: window.map_id,
    };
  }

  updatePresentationWaypointNodeIds({ presentationUUID, waypointUUID, nodeIds, prevNodeIds}) {
    return {
      operation: 'updatePresentationWaypointNodeIds',
      presentationUUID,
      waypointUUID,
      nodeIds,
      prevNodeIds,
      mapId: window.map_id,
    };
  }

  setCollapsedState({ nodeUUID, isCollapsed }) {
    let map = {};
    const node = store.getNode(nodeUUID);
    const nodes = [node];

    const filteredNodes = nodes
      .map(_node => {
        // remove the _store property
        const { _store, ...newNode } = _node;
        return newNode;
      })
      .map(n => {
        return {
          id: n.id,
          uuid: n.id,
          isCollapsed: n.isCollapsed,
          x: n.x,
          y: n.y,
          absolutePosition: n.absolutePosition,
          height: n.height,
          width: n.width,
          orderIndex: n.orderIndex,
          parentId: n.parentId,
        };
      });
    map.nodes = filteredNodes;

    return {
      operation: 'setCollapsedState',
      nodeUUID,
      map,
      isCollapsed
    };
  }

  editPerspectivesOrderCollection({ sortedPs, prevPs }) {
    return {
      operation: 'editPerspectivesOrderCollection',
      sortedPs,
      prevPs
    };
  }

  /* @@ operation-create-message @@ */

  addRegion({ regionUUID, title, color, top, left, right, bottom }) {
    return {
      operation: 'addRegion',
      regionUUID,
      title,
      color,
      top,
      left,
      right,
      bottom,
    };
  }

  updateRegion({ regionUUID, updates, prevValues }) {
    return {
      operation: 'updateRegion',
      updates,
      prevValues,
      regionUUID,
    };
  }

  deleteRegion({ regionUUID, title, color, top, left, right, bottom }) {
    return {
      operation: 'deleteRegion',
      regionUUID,
      title,
      color,
      top,
      left,
      right,
      bottom,
    };
  }

  undeleteRegion({ regionUUID, title, color, top, left, right, bottom }) {
    return {
      operation: 'deleteRegion',
      regionUUID,
      title,
      color,
      top,
      left,
      right,
      bottom,
    };
  }

  addPerspectivePoint({ perspectiveUUID, members, label, color, orderIndex, pointMapNodeUUID }) {
    return {
      operation: 'addPerspectivePoint',
      perspectiveUUID,
      label,
      members,
      color,
      orderIndex,
      pointMapNodeUUID
    };
  }

  deletePerspectivePoint({ perspectiveUUID }) {
    return {
      operation: 'deletePerspectivePoint',
      perspectiveUUID
    };
  }

  undeletePerspectivePoint({ perspectiveUUID }) {
    return {
      operation: 'undeletePerspectivePoint',
      perspectiveUUID
    };
  }

  updatePerspectivePoint({ perspectiveUUID, updates, prevValues }) {
    return {
      operation: 'updatePerspectivePoint',
      perspectiveUUID,
      updates,
      prevValues
    };
  }

  addToPerspectiveView({ perspectiveUUID, nodeIds, linkIds, overrides }) {
    return {
      operation: 'addToPerspectiveView',
      perspectiveUUID,
      nodeIds,
      linkIds,
      overrides,
    };
  }

  deleteFromPerspectiveView({ perspectiveUUID, nodeIds, linkIds }) {
    return {
      operation: 'deleteFromPerspectiveView',
      perspectiveUUID,
      nodeIds,
      linkIds
    };
  }

  undeleteFromPerspectiveView({ perspectiveUUID, nodeIds, linkIds }) {
    return {
      operation: 'undeleteFromPerspectiveView',
      perspectiveUUID,
      nodeIds,
      linkIds
    };
  }

  updatePerspectiveNodeViews({ nodeViews, prevNodeViews }) {
    return {
      operation: 'updatePerspectiveNodeViews',
      nodeViews,
      prevNodeViews,
    };
  }

  addCardRole({ role }) {
    return {
      operation: 'addCardRole',
      role,
    };
  }

  addCardRoleAttribute({ role, name, displayName, description, peculiarities }) {
    return {
      operation: 'addCardRoleAttribute',
      role,
      name,
      displayName,
      description,
      peculiarities,
    };
  }

  getCardRoles() {
    return {
      operation: 'getCardRoles',
    };
  }

  addMapNodeRole({ nodeId, role }) {
    return {
      operation: 'addMapNodeRole',
      mapNodeUUID: nodeId,
      role,
    };
  }

  removeMapNodeRole({ nodeId, role }) {
    return {
      operation: 'removeMapNodeRole',
      mapNodeUUID: nodeId,
      role,
    };
  }

  getMapNodeRoles({ nodeId }) {
    return {
      operation: 'getMapNodeRoles',
      mapNodeUUID: nodeId,
    };
  }

  addMapNodeAttribute({ uuid, mapNodeUUID, role, name, value }) {
    return {
      operation: 'addMapNodeAttribute',
      uuid,
      mapNodeUUID,
      role,
      name,
      value,
    };
  }

  editMapNodeAttribute({ uuid, mapNodeUUID, role, name, value, prevName, prevValue }) {
    return {
      operation: 'editMapNodeAttribute',
      uuid,
      mapNodeUUID,
      role,
      name,
      value,
      prevName,
      prevValue,
    };
  }

  removeMapNodeAttribute({ uuid, mapNodeUUID, name, value, role }) {
    return {
      operation: 'removeMapNodeAttribute',
      uuid,
      mapNodeUUID,
      name,
      value,
      role,
    };
  }

  getMapNodeAttributes({ nodeId }) {
    return {
      operation: 'getMapNodeAttributes',
      mapNodeUUID: nodeId,
    };
  }

  addComment({ uuid, comment }) {
    return {
      operation: 'addComment',
      uuid,
      comment,
    };
  }

  editComment({ uuid, mapId, commentText, prevCommentText }) {
    return {
      operation: 'editComment',
      uuid,
      mapId,
      commentText,
      prevCommentText,
    };
  }

  deleteComment({ uuid }) {
    return {
      operation: 'deleteComment',
      uuid,
    };
  }

  undeleteComment({ uuid }) {
    return {
      operation: 'undeleteComment',
      uuid,
    };
  }

  resolveComment({ uuid }) {
    return {
      operation: 'resolveComment',
      uuid,
    };
  }

  unresolveComment({ uuid }) {
    return {
      operation: 'unresolveComment',
      uuid,
    };
  }

  updateCommentDisplayProperties({ uuid, displayProperties, prevDisplayProperties }) {
    return {
      operation: 'updateCommentDisplayProperties',
      uuid,
      displayProperties,
      prevDisplayProperties,
    };
  }

  updateMapDisplayProperties({ displayProperties, prevDisplayProperties }) {
    return {
      operation: 'updateMapDisplayProperties',
      displayProperties,
      prevDisplayProperties,
    };
  }

  loadOperations() {
    return {
      operation: 'loadOperations',
    };
  }

  addBatch({ nodes, links }) {
    const filteredNodes = nodes.map(translateNode);
    const filteredLinks = links.map(translateLink);

    return {
      operation: 'addBatch',
      nodes: filteredNodes,
      links: filteredLinks,
    };
  }

  shareMapWithUser({ email, permission }) {
    return {
      operation: 'shareMapWithUser',
      email,
      permission,
    };
  }

  updateUserShareSettings({ sharedWithUserId, inviteId, permission }) {
    return {
      operation: 'updateUserShareSettings',
      sharedWithUserId,
      inviteId,
      permission,
    };
  }


}


class UndoRedo {
  constructor () {
    this.undoStack = [];
    this.redoStack = [];
    this.inUndoOrRedo = false;
  }


  invert(message) {
    // remove private attributes from inverted message
    for (const field in message) {
      if (field.match(/^_/)) {
        delete message[field];
      }
    }

    switch (message.operation) {
      case 'setMapTitle':
        return this.invertSetMapTitle(message);
      case 'addNode':
        return this.invertAddNode(message);
      case 'deleteNodes':
        return this.invertDeleteNodes(message);
      case 'undeleteNodes':
        return this.invertUndeleteNodes(message);
      case 'addBatch':
        return this.invertAddBatch(message);
      case 'undeleteLink':
        return this.invertUndeleteLink(message);
      case 'addLink':
        return this.invertAddLink(message);
      case 'deleteLink':
        return this.invertDeleteLink(message);
      case 'updateNodePositions':
        return this.invertUpdateNodePositions(message);
      case 'updateNodeDimensions':
        return this.invertUpdateNodeDimensions(message);
      case 'updateNodeJustification':
        return this.invertUpdateNodeJustification(message);
      case 'addPresentationWaypoint':
        return this.invertAddPresentationWaypoint(message);
      case 'addPresentationWaypoints':
        return this.invertAddPresentationWaypoints(message);
      case 'removePresentationWaypoint':
        return this.invertRemovePresentationWaypoint(message);
      case 'removePresentationWaypoints':
        return this.invertRemovePresentationWaypoints(message);
      case 'clearPresentationWaypoints':
        return this.invertClearPresentationWaypoints(message);
      case 'updatePresentationWaypointOrderIndexes':
        return this.invertUpdatePresentationWaypointOrderIndexes(message);
      case 'updatePresentationWaypointTitle':
        return this.invertUpdatePresentationWaypointTitle(message);
      case 'updatePresentationWaypointNodeIds':
        return this.invertUpdatePresentationWaypointNodeIds(message);
      case 'setLinkEndpoints':
        return this.invertSetLinkEndpoints(message);
      case 'setLinkAngles':
        return this.invertSetLinkAngles(message);
      case 'setChildrenPosition':
        return this.invertSetChildrenPosition(message);
      case 'setChildrenDetached':
        return this.invertSetChildrenDetached(message);
      case 'setCollapsedState':
        return this.invertSetCollapsedState(message);
      case 'editNode':
        return this.invertEditNode(message);
      case 'editPerspectivesOrderCollection':
        return this.invertEditPerspectivesOrderCollection(message);
      /* @@ operation-call-invert @@ */
      case 'addRegion':
        return this.invertAddRegion(message);
      case 'updateRegion':
        return this.invertUpdateRegion(message);
      case 'deleteRegion':
        return this.invertDeleteRegion(message);
      case 'undeleteRegion':
        return this.invertUndeleteRegion(message);
      case 'addPerspectivePoint':
        return this.invertAddPerspectivePoint(message);
      case 'deletePerspectivePoint':
        return this.invertDeletePerspectivePoint(message);
      case 'undeletePerspectivePoint':
        return this.invertUndeletePerspectivePoint(message);
      case 'updatePerspectivePoint':
        return this.invertUpdatePerspectivePoint(message);
      case 'addToPerspectiveView':
        return this.invertAddToPerspectiveView(message);
      case 'deleteFromPerspectiveView':
        return this.invertDeleteFromPerspectiveView(message);
      case 'undeleteFromPerspectiveView':
        return this.invertUndeleteFromPerspectiveView(message);
      case 'updatePerspectiveNodeViews':
        return this.invertUpdatePerspectiveNodeViews(message);
      case 'setLinkDirection':
        return this.invertSetLinkDirection(message);
      case 'setLinkColor':
        return this.invertSetLinkColor(message);
      case 'setLinkType':
        return this.invertSetLinkType(message);
      case 'setLinkWidth':
        return this.invertSetLinkWidth(message);
      case 'setLinkCurvature':
        return this.invertSetLinkCurvature(message);
      case 'setLinkNodeId':
        return this.invertSetLinkNodeId(message);
      case 'toggleLinkPreviewHidden':
        return this.invertToggleLinkPreviewHidden(message);
      case 'toggleLinkPreviewCollapsed':
        return this.invertToggleLinkPreviewCollapsed(message);
      case 'setNodeLayoutName':
        return this.invertSetNodeLayoutName(message);
      case 'setNodeBackgroundColor':
        return this.invertSetNodeBackgroundColor(message);
      case 'setNodeDisplayStyle':
        return this.invertSetNodeDisplayStyle(message);
      case 'batchOperation':
        return this.invertBatchOperation(message);
      case 'rotateNodeFile':
        return this.invertRotateNodeFile(message);
      case 'addFileToNode':
        return this.invertAddFileToNode(message);
      case 'deleteNodeFile':
        return this.invertDeleteNodeFile(message);
      case 'undeleteNodeFile':
        return this.invertUndeleteNodeFile(message);
      case 'updateNodeFile':
        return this.invertUpdateNodeFile(message);
      case 'addMapNodeAttribute':
        return this.invertAddMapNodeAttribute(message);
      case 'editMapNodeAttribute':
        return this.invertEditMapNodeAttribute(message);
      case 'removeMapNodeAttribute':
        return this.invertRemoveMapNodeAttribute(message);
      case 'addComment':
        return this.invertAddComment(message);
      case 'editComment':
        return this.invertEditComment(message);
      case 'resolveComment':
        return this.invertResolveComment(message);
      case 'unresolveComment':
        return this.invertUnresolveComment(message);
      case 'deleteComment':
        return this.invertDeleteComment(message);
      case 'undeleteComment':
        return this.invertUndeleteComment(message);
      case 'updateCommentDisplayProperties':
        return this.invertUpdateCommentDisplayProperties(message);
      case 'updateMapDisplayProperties':
        return this.invertUpdateMapDisplayProperties(message);
      default:
        console.log(`unknown operation: ${message.operation}`);
        return;
    }
  }

  trackUndoable(message) {
    // if this message isn't coming from an undo or redo (it was an explicit
    // action taken by this user on the canvas), then we want to zero out the
    // redoStack (you can't redo something that isn't an undo), and store the
    // inverse of this operation on the undoStack.

    //I know this probably shouldn't be here but.......it would have to be a lot of other places too
    canvas.closeUndoBanner();

    if (!this.inUndoOrRedo) {
      const inverse = this.invert(message);

      // extra bits for historyUI
      inverse.userId = window.user_id;
      inverse.user = { id: window.user_id };
      inverse.createdTime = Date.now();

      this.redoStack = [];
      inverse.undo = true;
      this.undoStack.push(inverse);
      canvas.undoRedoTag && canvas.undoRedoTag.update();
    }
  }

  invertRotateNodeFile(message) {
    const inverse = this.invertAddFileToNode(message);
    inverse.operation = 'rotateNodeFile';
    return inverse;
  }

  invertAddFileToNode(message) {
    const inverse = Object.assign({}, message);

    if (message.prevFile) {
      let newPrevFile = {
        nodeUUID: message.nodeUUID,
        fileUUID: message.fileUUID,
        fileUrl: message.fileUrl,
        fileType: message.fileType,
        fileSize: message.fileSize,
        fileAttributes: Object.assign({},message.fileAttributes),
        displayProperties: Object.assign({},message.displayProperties),
        previewFileUrl: message.previewFileUrl,
        name: message.name,
      };
      const prevFile = message.prevFile;

      inverse.fileUUID = prevFile.fileUUID;
      inverse.fileUrl = prevFile.fileUrl;
      inverse.fileType = prevFile.fileType;
      inverse.fileSize = prevFile.fileSize;
      inverse.fileAttributes = Object.assign({},prevFile.fileAttributes);
      inverse.displayProperties = Object.assign({},prevFile.displayProperties);
      inverse.previewFileUrl = prevFile.previewFileUrl;
      inverse.name = prevFile.name;
      inverse.prevFile = newPrevFile;
    } else {
      inverse.operation = 'deleteNodeFile';
    }
    return inverse;
  }

  invertDeleteNodeFile(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeleteNodeFile';
    return inverse;
  }

  invertUndeleteNodeFile(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteNodeFile';
    return inverse;
  }

  invertUpdateNodeFile(message) {
    const inverse = Object.assign({}, message);
    inverse.displayProperties = message.prevDisplayProperties;
    inverse.prevDisplayProperties = message.displayProperties;
    return inverse;
  }

  invertAddMapNodeAttribute(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'removeMapNodeAttribute';
    return inverse;
  }

  invertRemoveMapNodeAttribute(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'addMapNodeAttribute';
    return inverse;
  }

  invertEditMapNodeAttribute(message) {
    const inverse = Object.assign({}, message);
    inverse.name = message.prevName;
    inverse.value = message.prevValue;
    inverse.prevName = message.name;
    inverse.prevValue = message.value;
    return inverse;
  }

  invertAddComment(message) {
    const inverse = {
      uuid: message.comment.uuid,
      operation: 'deleteComment',
    };
    return inverse;
  }

  invertEditComment(message) {
    const inverse = Object.assign({}, message);
    inverse.commentText = message.prevCommentText;
    inverse.prevCommentText = message.commentText;
    return inverse;
  }

  invertDeleteComment(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeleteComment';
  }

  invertUndeleteComment(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteComment';
  }

  invertResolveComment(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'unresolveComment';
  }

  invertUnresolveComment(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'resolveComment';
  }

  invertUpdateCommentDisplayProperties(message) {
    const inverse = Object.assign({}, message);
    inverse.displayProperties = message.prevDisplayProperties;
    inverse.prevDisplayProperties = message.displayProperties;
    return inverse;
  }

  invertSetMapTitle(message) {
    const inverse = Object.assign({}, message);
    inverse.title = message.prevTitle;
    inverse.prevTitle = message.title;
    return inverse;
  }

  invertAddNode(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteNodes';
    inverse.nodeIds = [message.node.id];
    return inverse;
  }

  invertDeleteNodes(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeleteNodes';
    return inverse;
  }

  invertAddBatch(message) {
    const inverse = Object.assign({}, message);
    inverse.linkIds = message.links.map(l => l.id);
    inverse.nodeIds = message.nodes.map(n => n.id);
    delete inverse.nodes;
    delete inverse.links;
    inverse.operation = 'deleteNodes';
    return inverse;
  }

  invertUndeleteNodes(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteNodes';
    return inverse;
  }

  invertAddLink(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteLink';
    inverse.linkId = message.link.id;
    return inverse;
  }

  invertDeleteLink(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeleteLink';
    return inverse;
  }

  invertUndeleteLink(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteLink';
    return inverse;
  }

  invertUpdateNodePositions(message) {
    const inverse = Object.assign({}, message);
    inverse.nodePositions = message.prevNodePositions;
    inverse.linkAngles = message.prevLinkAngles;
    inverse.prevNodePositions = message.nodePositions;
    inverse.prevLinkAngles = message.linkAngles;
    return inverse;
  }

  invertUpdateNodeDimensions(message) {
    const inverse = Object.assign({}, message);
    inverse.prevUserWidth = message.userWidth || null;
    inverse.prevUserHeight = message.userHeight || null;
    inverse.userWidth = message.prevUserWidth || null;
    inverse.userHeight = message.prevUserHeight || null;
    return inverse;
  }

  invertUpdateNodeJustification(message) {
    const inverse = Object.assign({}, message);
    inverse.prevJustification = message.justification;
    inverse.justification = message.prevJustification;
    return inverse;
  }

  invertAddPresentationWaypoint(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'removePresentationWaypoint';
    return inverse;
  }

  invertAddPresentationWaypoints(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'removePresentationWaypoints';
    return inverse;
  }

  invertRemovePresentationWaypoint(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'addPresentationWaypoint';
    return inverse;
  }

  invertRemovePresentationWaypoints(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'addPresentationWaypoints';
    return inverse;
  }

  invertClearPresentationWaypoints(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'addPresentationWaypoints';
    inverse.waypoints = message.prevWaypoints;
    inverse.prevWaypoints = [];
    return inverse;
  }

  invertUpdatePresentationWaypointOrderIndexes(message) {
    const inverse = Object.assign({}, message);
    inverse.idIndexMappings = message.prevIdIndexMappings;
    inverse.prevIdIndexMappings = message.idIndexMappings;
    return inverse;
  }

  invertUpdatePresentationWaypointTitle(message) {
    const inverse = Object.assign({}, message);
    inverse.title = message.prevTitle;
    inverse.prevTitle = message.title;
    return inverse;
  }

  invertUpdatePresentationWaypointNodeIds(message) {
    const inverse = Object.assign({}, message);
    inverse.nodeIds = message.prevNodeIds;
    inverse.prevNodeIds = message.nodeIds;
    return inverse;
  }

  invertEditNode(message) {
    const inverse = Object.assign({}, message);
    inverse.prevValues = message.updates;
    inverse.updates = message.prevValues;
    return inverse;
  }

  invertSetCollapsedState(message) {
    const inverse = Object.assign({}, message);
    inverse.isCollapsed = !message.isCollapsed;
    if (message.map && message.map.nodes) {
      inverse.map = {
        nodes: []
      };
      for (const node of message.map.nodes) {
        inverse.map.nodes.push( Object.assign({},node,{
          isCollapsed: !node.isCollapsed
        }));
      }
    }
    return inverse;
  }

  invertEditPerspectivesOrderCollection(message) {
    const inverse = Object.assign({}, message);
    inverse.sortedPs = message.prevPs;
    inverse.prevPs = message.sortedPs;
    return inverse;
  }

  /* @@ operation-invert @@ */

  invertAddRegion(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteRegion';
    return inverse;
  }

  invertUpdateRegion(message) {
    const inverse = Object.assign({}, message);
    inverse.updates = message.prevValues;
    inverse.prevValues = message.updates;
    return inverse;
  }

  invertDeleteRegion(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeleteRegion';
    return inverse;
  }

  invertUndeleteRegion(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteRegion';
    return inverse;
  }

  invertAddPerspectivePoint(message) {
    const inverse = {};
    inverse.operation = 'deletePerspectivePoint';
    inverse.perspectiveUUID = message.perspectiveUUID;
    return inverse;
  }

  invertDeletePerspectivePoint(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeletePerspectivePoint';
    return inverse;
  }

  invertUndeletePerspectivePoint(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deletePerspectivePoint';
    return inverse;
  }

  invertUpdatePerspectivePoint(message) {
    const inverse = Object.assign({}, message);
    inverse.prevValues = message.updates;
    inverse.updates = message.prevValues;
    return inverse;
  }

  invertAddToPerspectiveView(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteFromPerspectiveView';
    return inverse;
  }

  invertDeleteFromPerspectiveView(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'undeleteFromPerspectiveView';
    return inverse;
  }

  invertUndeleteFromPerspectiveView(message) {
    const inverse = Object.assign({}, message);
    inverse.operation = 'deleteFromPerspectiveView';
    return inverse;
  }

  invertUpdatePerspectiveNodeViews(message) {
    const inverse = Object.assign({}, message);
    inverse.nodeViews = message.prevNodeViews;
    inverse.prevNodeViews = message.nodeViews;
    return inverse;
  }

  invertSetLinkDirection(message) {
    const inverse = Object.assign({}, message);
    inverse.direction = message.prevDirection;
    inverse.prevDirection = message.direction;
    return inverse;
  }

  invertSetLinkColor(message) {
    const inverse = Object.assign({}, message);
    inverse.color = message.prevColor;
    inverse.prevColor = message.color;
    return inverse;
  }

  invertSetLinkType(message) {
    const inverse = Object.assign({}, message);
    if (!message.prevLineType) {
      inverse.lineType = DEFAULTS.LINK_TYPE;
    } else {
      inverse.lineType = message.prevLineType;
    }
    inverse.prevLineType = message.lineType;
    return inverse;
  }

  invertSetLinkWidth(message) {
    const inverse = Object.assign({}, message);
    if (!message.prevLineWidth) {
      inverse.lineWidth = DEFAULTS.LINK_WIDTH;
    } else {
      inverse.lineWidth = message.prevLineWidth;
    }
    inverse.prevLineWidth = message.lineWidth;
    return inverse;
  }

  invertSetLinkCurvature(message) {
    const inverse = Object.assign({}, message);

    if (!message.prevLineCurvature) {
      inverse.lineCurvature = DEFAULTS.LINK_CURVATURE;
    } else {
      inverse.lineCurvature = message.prevLineCurvature;
    }

    inverse.prevLineCurvature = message.lineCurvature;

    return inverse;
  }

  invertSetLinkNodeId(message) {
    const inverse = Object.assign({}, message);
    inverse.link = message.prevLink;
    inverse.prevLink = message.link;
    return inverse;
  }

  invertToggleLinkPreviewHidden(message) {
    const inverse = Object.assign({}, message);
    inverse.isHidden = !message.isHidden;
    return inverse;
  }

  invertToggleLinkPreviewCollapsed(message) {
    const inverse = Object.assign({}, message);
    inverse.isCollapsed = !message.isCollapsed;
    return inverse;
  }

  invertSetNodeBackgroundColor(message) {
    const inverse = Object.assign({}, message);
    inverse.color = message.prevColor;
    inverse.prevColor = message.color;
    return inverse;
  }

  invertSetNodeDisplayStyle(message) {
    const inverse = Object.assign({}, message);
    inverse.style = message.prevStyle;
    inverse.prevStyle = message.style;
    return inverse;
  }

  invertSetNodeLayoutName(message) {
    const inverse = Object.assign({}, message);
    inverse.layoutName = message.prevLayoutName;
    inverse.prevLayoutName = message.layoutName;
    return inverse;
  }

  invertSetChildrenPosition(message) {
    const inverse = Object.assign({}, message);
    inverse.prevX = message.x;
    inverse.x = message.prevX;
    inverse.prevY = message.y;
    inverse.y = message.prevY;
    inverse.userWidth = message.prevUserWidth;
    inverse.userHeight = message.prevUserHeight;
    inverse.prevUserHeight = message.userHeight;
    inverse.prevUserWidth = message.userWidth;
    return inverse;
  }

  invertSetChildrenDetached(message) {
    const inverse = Object.assign({}, message);
    inverse.detached = !message.detached;
    return inverse;
  }

  invertSetLinkEndpoints(message) {
    const inverse = Object.assign({}, message);
    inverse.fromNodeId = message.prevFromNodeId;
    inverse.prevFromNodeId = message.fromNodeId;
    inverse.toNodeId = message.prevToNodeId;
    inverse.prevToNodeId = message.toNodeId;
    return inverse;
  }

  invertSetLinkAngles(message) {
    const inverse = Object.assign({}, message);
    inverse.angleStartToR = message.prevAngleStartToR;
    inverse.prevAngleStartToR = message.angleStartToR;
    inverse.angleEndToR = message.prevAngleEndToR;
    inverse.prevAngleEndToR = message.angleEndToR;
    return inverse;
  }

  invertUpdateMapDisplayProperties(message) {
    const inverse = Object.assign({}, message);
    inverse.displayProperties = message.prevDisplayProperties;
    inverse.prevDisplayProperties = message.displayProperties;
    return inverse;
  }

  invertBatchOperation(message) {
    const inverse = Object.assign({}, message);
    const invertedMessages = [];
    if (message.messages && message.messages.length) {
      for (const msg of message.messages) {
        const imsg = this.invert(msg);
        if (!imsg) return;
        invertedMessages.push(imsg);
      }
    }
    inverse.messages = invertedMessages.reverse();
    return inverse;
  }

}

export { MapOperation };



// WEBPACK FOOTER //
// ./starling/src/operation.js